import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import Icon from '../components/icon';
import SelectField from '../components/select-field';
import SectionTabs from '../components/section-tabs';
import {
  dismissManager,
  getDivision,
  getManager,
  listClientTrustedPersons,
  listDivisions,
  listManagerClients,
  loginAsClient,
  moveManagerDivision,
} from '../api/panel-api';
import ManagerFeed from './manager-feed';
import { DivisionPropertiesDialog } from './manager-divisions';
import {
  DismissDialog,
  DivisionSelect,
  isConsultant,
  isRd,
  loadWorkingStaff,
  MoveRdDialog,
  dismissToast,
  moveToast,
  personLabel,
} from './manager-hr';
import ManagerOrders from './manager-orders';
import ManagerStats from './manager-stats';
import { formatDateTime, formatPhone, serviceRoleLabel } from '../data/panel';
import { managersPath } from '../section-url';

const TABS = [
  { id: 'clients', label: 'Клиенты' },
  { id: 'orders', label: 'Заказы' },
  { id: 'statistics', label: 'Статистика' },
  { id: 'feed', label: 'Лента событий' },
];

const TYPE_FILTERS = [
  { value: 'all', label: 'Все' },
  { value: 'pa', label: 'ПА-физлица' },
  { value: 'ra', label: 'РА-физлица' },
  { value: 'legal', label: 'Юрлица' },
];

const TYPE_EMPTY = {
  pa: 'Среди закреплённых клиентов нет ПА-физлиц. Выберите другой тип клиента.',
  ra: 'Среди закреплённых клиентов нет РА-физлиц. Выберите другой тип клиента.',
  legal: 'Среди закреплённых клиентов нет юрлиц. Выберите другой тип клиента.',
};

const ROMAN = ['I', 'II', 'III', 'IV'];

function quarterStamp(start) {
  if (!start) return '';
  const month = Number(String(start).slice(5, 7));
  const year = String(start).slice(0, 4);
  const roman = ROMAN[Math.floor((month - 1) / 3)] || '';
  return `${roman} / ${year}`;
}

function formatMoney(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return '0 ₽';
  const text = Math.round(n).toLocaleString('ru-RU').replace(/\u00A0/g, ' ');
  return `${text} ₽`;
}

function formatCount(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return '0';
  return Math.round(n).toLocaleString('ru-RU').replace(/\u00A0/g, ' ');
}

function clientName(item) {
  if (isLegal(item)) return item.company_name || 'Юрлицо';
  return `${item.surname || ''} ${item.name || ''}`.trim() || 'Клиент';
}

function isLegal(item) {
  return item.user_type === 'legal_entity' || (Boolean(item.company_name) && item.user_type !== 'individual');
}

function daysUntilBlock(item) {
  const n = Number(item.days_until_block);
  return Number.isFinite(n) && n > 0 ? n : 0;
}

function daysLabel(n) {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return `${n} день до блокировки`;
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return `${n} дня до блокировки`;
  return `${n} дней до блокировки`;
}

function paIcon(status) {
  if (status === 'confirmed') return 'pa-confirmed';
  if (status === 'notConfirmed') return 'pa-pending';
  if (status === 'uncertain') return 'pa-uncertain';
  if (status === 'blocked') return 'pa-blocked';
  return '';
}

function typeFilters(type) {
  if (type === 'pa') return { user_type: 'individual', client_type: 'professional' };
  if (type === 'ra') return { user_type: 'individual', client_type: 'retail' };
  if (type === 'legal') return { user_type: 'legal_entity' };
  return {};
}

function tabOf(value) {
  return TABS.some((item) => item.id === value) ? value : 'clients';
}

function PagerPages({ page, pageCount, onPage }) {
  if (pageCount <= 1) {
    return <span className="counter counter--page">{page}</span>;
  }
  const size = Math.min(pageCount, 7);
  const start = Math.max(1, Math.min(page - 3, pageCount - size + 1));
  const pages = [];
  for (let index = 0; index < size; index += 1) pages.push(start + index);
  return (
    <span className="pager__pages">
      {pages.map((item) => (
        <button
          key={item}
          type="button"
          className={item === page ? 'pager__page is-current' : 'pager__page'}
          aria-current={item === page ? 'page' : undefined}
          onClick={() => onPage(item)}
        >
          {item}
        </button>
      ))}
    </span>
  );
}

function ManagerCardPage({ sectionCode, managerId, tab, role, currentUserId, onLocation }) {
  const currentTab = tabOf(tab);
  const [manager, setManager] = useState(null);
  const [divisionTitle, setDivisionTitle] = useState('');
  const [error, setError] = useState('');
  const [clients, setClients] = useState([]);
  const [clientTotal, setClientTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(30);
  const [sort, setSort] = useState('last_order_at');
  const [order, setOrder] = useState('desc');
  const [clientType, setClientType] = useState('all');
  const [clientsLoading, setClientsLoading] = useState(false);
  const [clientsError, setClientsError] = useState('');
  const [trusted, setTrusted] = useState(null);
  const [loginError, setLoginError] = useState('');
  const [canHr, setCanHr] = useState(false);
  const [divisions, setDivisions] = useState([]);
  const [menu, setMenu] = useState(false);
  const [moveDialog, setMoveDialog] = useState(null);
  const [dismissOpen, setDismissOpen] = useState(false);
  const [recipients, setRecipients] = useState([]);
  const [createOpen, setCreateOpen] = useState(false);
  const [hrPending, setHrPending] = useState(false);
  const [hrError, setHrError] = useState('');
  const [toast, setToast] = useState(null);
  const [emptyLead, setEmptyLead] = useState('');
  const menuRef = useRef(null);
  const canLogin = role === 'administrator' || role === 'cs_employee';
  const ownCard = Boolean(manager && currentUserId && manager.user_id === currentUserId);
  const working = manager && manager.status !== 'dismissed';
  const canComment = (role === 'administrator' || role === 'cs_employee')
    && working
    && !(ownCard && (manager.cs_role === 'manager' || manager.cs_role === 'consultant'));
  const canChangeDivision = Boolean(
    canHr && working && manager && !isConsultant(manager) && manager.cs_role !== 'rks',
  );
  const canDismiss = Boolean(
    canHr && working && manager && !ownCard && manager.cs_role !== 'rks',
  );

  useEffect(() => {
    let cancelled = false;
    setError('');
    getManager(managerId)
      .then((data) => {
        if (!cancelled) applyManager(data);
      })
      .catch((loadError) => {
        if (!cancelled) {
          setManager(null);
          setError(loadError.message);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [managerId]);

  useEffect(() => {
    let cancelled = false;
    listDivisions()
      .then((data) => {
        if (cancelled) return;
        setCanHr(role === 'administrator' || role === 'cs_employee');
        setDivisions(data.items || []);
      })
      .catch(() => {
        if (cancelled) return;
        setCanHr(false);
        setDivisions([]);
      });
    return () => {
      cancelled = true;
    };
  }, [managerId, role]);

  useEffect(() => {
    if (!menu) return undefined;
    function onDoc(event) {
      if (menuRef.current && !menuRef.current.contains(event.target)) setMenu(false);
    }
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [menu]);

  useEffect(() => {
    if (!toast) return undefined;
    const timer = window.setTimeout(() => setToast(null), 5000);
    return () => window.clearTimeout(timer);
  }, [toast]);

  useEffect(() => {
    if (currentTab !== 'clients' || !manager) return undefined;
    let cancelled = false;
    setClientsLoading(true);
    listManagerClients({
      id: managerId,
      page,
      perPage: pageSize,
      sort,
      order,
      filters: typeFilters(clientType),
    })
      .then((data) => {
        if (cancelled) return;
        setClients(data.items || []);
        setClientTotal(data.total || 0);
        setClientsError('');
      })
      .catch((loadError) => {
        if (cancelled) return;
        setClients([]);
        setClientsError(loadError.message);
      })
      .finally(() => {
        if (!cancelled) setClientsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [currentTab, manager, managerId, page, pageSize, sort, order, clientType]);

  function applyManager(data) {
    setManager(data);
    const nested = data.division && data.division.title;
    if (nested) {
      setDivisionTitle(nested);
      return;
    }
    if (!data.division_id) {
      setDivisionTitle('Вне дивизиона');
      return;
    }
    const found = divisions.find((item) => item.id === data.division_id);
    if (found) {
      setDivisionTitle(found.title);
      return;
    }
    getDivision(data.division_id)
      .then((division) => setDivisionTitle(division.title || 'Вне дивизиона'))
      .catch(() => setDivisionTitle(''));
  }

  async function reloadManager() {
    const data = await getManager(managerId);
    applyManager(data);
    return data;
  }

  async function refreshDivisions() {
    const data = await listDivisions();
    setDivisions(data.items || []);
  }

  async function applyDivision(nextId) {
    const currentId = manager.division_id || null;
    if ((nextId || null) === currentId) return;
    const target = nextId ? divisions.find((item) => item.id === nextId) : null;
    const outside = !nextId;
    if (isRd(manager) && currentId) {
      setMoveDialog({ nextId: nextId || null, target, outside });
      setHrError('');
      return;
    }
    await confirmMove(nextId || null, target);
  }

  async function confirmMove(nextId, target) {
    setHrPending(true);
    setHrError('');
    try {
      await moveManagerDivision(managerId, nextId || null);
      await refreshDivisions();
      await reloadManager();
      setMoveDialog(null);
      setToast({ title: moveToast(manager, target && target.title) });
    } catch (actionError) {
      setHrError(actionError.message);
    } finally {
      setHrPending(false);
    }
  }

  async function openDismiss() {
    setMenu(false);
    setHrError('');
    try {
      const staff = await loadWorkingStaff();
      setRecipients(staff.filter((item) => (
        item.id !== manager.id
        && item.cs_role !== 'consultant'
        && item.status !== 'dismissed'
      )));
      setDismissOpen(true);
    } catch (actionError) {
      setHrError(actionError.message);
    }
  }

  async function confirmDismiss(body, outcome) {
    setHrPending(true);
    setHrError('');
    try {
      await dismissManager(managerId, body);
      const notice = dismissToast(manager, outcome);
      setEmptyLead(notice.emptyLead);
      setToast({ title: notice.title });
      setDismissOpen(false);
      await reloadManager();
    } catch (actionError) {
      setHrError(actionError.message);
    } finally {
      setHrPending(false);
    }
  }

  async function onDivisionCreated(created) {
    setCreateOpen(false);
    await refreshDivisions();
    const data = await reloadManager();
    if (data.division_id === created.id) {
      setToast({ title: moveToast(data, created.title) });
    }
  }

  function changeTab(id) {
    onLocation({ view: 'card', managerId, tab: id === 'clients' ? '' : id });
  }

  function changeSort(field) {
    if (sort === field) {
      setOrder((dir) => (dir === 'asc' ? 'desc' : 'asc'));
      return;
    }
    setSort(field);
    setOrder(field === 'last_order_at' || field === 'purchases_total' || field === 'orders_count' ? 'desc' : 'asc');
  }

  async function openLogin(item) {
    setLoginError('');
    if (isLegal(item)) {
      try {
        const data = await listClientTrustedPersons(item.id);
        const people = (data.items || []).filter((person) => person.user_active !== false);
        if (people.length === 1) {
          await enterAs(people[0].id, item.id);
          return;
        }
        if (!people.length) {
          setLoginError('Вход выполняется под доверенным лицом юрлица.');
          return;
        }
        setTrusted({ legalId: item.id, people });
      } catch (actionError) {
        setLoginError(actionError.message);
      }
      return;
    }
    await enterAs(item.id);
  }

  async function enterAs(clientId, legalId) {
    try {
      const data = await loginAsClient(clientId, legalId ? { legal_entity_id: legalId } : undefined);
      setTrusted(null);
      if (data && data.url) window.open(data.url, '_blank', 'noopener,noreferrer');
    } catch (actionError) {
      setLoginError(actionError.message);
    }
  }

  if (error) {
    return (
      <section className="user-page" aria-label="Менеджер">
        <BackLink sectionCode={sectionCode} onLocation={onLocation} />
        <div className="empty"><p>{error}</p></div>
      </section>
    );
  }

  if (!manager) {
    return (
      <section className="user-page" aria-label="Менеджер" aria-busy="true">
        <BackLink sectionCode={sectionCode} onLocation={onLocation} />
      </section>
    );
  }

  const assigned = formatDateTime(manager.cs_assigned_at);
  const clientsCount = Number(manager.clients_count) || 0;
  const ordersCount = manager.metrics && manager.metrics.current ? manager.metrics.current.orders : null;
  const currentSales = manager.metrics && manager.metrics.current ? manager.metrics.current.sales : 0;
  const currentQuarter = manager.metrics && manager.metrics.current_quarter_start
    ? manager.metrics.current_quarter_start
    : '';
  const noneAtAll = clientType === 'all' && clientsCount === 0;
  const noneFiltered = clientType !== 'all' && !clientsLoading && clientTotal === 0;
  const pageCount = Math.max(1, Math.ceil(clientTotal / pageSize));
  const from = clientTotal === 0 ? 0 : (page - 1) * pageSize + 1;
  const to = Math.min(page * pageSize, clientTotal);

  const tabs = TABS.map((item) => ({
    id: item.id,
    label: item.label,
    href: managersPath(sectionCode, { view: 'card', managerId, tab: item.id === 'clients' ? '' : item.id }),
    count: item.id === 'clients' ? clientsCount : item.id === 'orders' ? (Number.isFinite(Number(ordersCount)) ? Number(ordersCount) : 0) : null,
  }));

  return (
    <section className="user-page manager-card" aria-label={`${manager.last_name} ${manager.first_name}`}>
      <BackLink sectionCode={sectionCode} onLocation={onLocation} />
      <article className="card manager-head">
        <div className="manager-head__top">
          <h1 className="manager-head__name">{manager.last_name} {manager.first_name}</h1>
          <span className={working ? 'manager-head__badge' : 'manager-head__badge is-fired'}>
            {working ? 'Работает' : `Уволен ${formatDateTime(manager.updated_at).text}`}
          </span>
          {canDismiss ? (
            <div className="manager-head__tools" ref={menuRef}>
              <button
                type="button"
                className={menu ? 'icon-button icon-button--sheet is-pressed' : 'icon-button icon-button--sheet'}
                aria-label="Действия"
                aria-expanded={menu}
                onClick={() => setMenu((open) => !open)}
              >
                <span className="kebab" aria-hidden="true"><i /><i /><i /></span>
              </button>
              {menu ? (
                <div className="row-menu row-menu--card">
                  <button type="button" className="menu-row menu-row--danger" onClick={openDismiss}>
                    <Icon name="trash" size={16} tone="inherit" />
                    Уволить
                  </button>
                </div>
              ) : null}
            </div>
          ) : null}
        </div>
        {hrError ? <p className="empty">{hrError}</p> : null}
        <div className="manager-head__grid">
          <Meta label="Телефон" value={formatPhone(manager.phone) || manager.phone || '—'} />
          <Meta label="Электронная почта" value={manager.email || '—'} />
          <Meta label="Роль" value={serviceRoleLabel[manager.cs_role] || manager.cs_role} />
          {canChangeDivision ? (
            <DivisionSelect
              value={manager.division_id || ''}
              label={divisionTitle || (manager.division_id ? '—' : 'Вне дивизиона')}
              divisions={divisions}
              disabled={hrPending}
              onPick={(nextId) => applyDivision(nextId)}
              onCreate={() => { setHrError(''); setCreateOpen(true); }}
            />
          ) : (
            <Meta label="Дивизион" value={divisionTitle || (manager.division_id ? '—' : 'Вне дивизиона')} />
          )}
          <Meta
            label="Клиентов"
            value={formatCount(clientsCount)}
            note={manager.inactive_clients_count ? `Неактивных ФЛ: ${formatCount(manager.inactive_clients_count)}` : ''}
          />
          <Meta label="Назначение сотрудником КС:" value={assigned.text} />
          <Meta label="Продажи за всё время" value={formatMoney(manager.sales_all_time)} />
          <Meta
            label={`Продажи за текущий квартал (${quarterStamp(currentQuarter) || '—'})`}
            value={formatMoney(currentSales)}
          />
        </div>
      </article>
      <SectionTabs items={tabs} value={currentTab} onChange={changeTab} />
      <article className="card manager-tab">
        {currentTab === 'clients' ? (
          <>
            <div className="manager-tab__head">
              <h2>Клиенты</h2>
              {!noneAtAll ? (
                <div className="manager-tab__filter">
                  <SelectField
                    label="Тип клиента"
                    required={false}
                    value={clientType}
                    onChange={(value) => {
                      setClientType(value);
                      setPage(1);
                    }}
                    options={TYPE_FILTERS}
                  />
                </div>
              ) : null}
            </div>
            {clientsError ? <p className="empty">{clientsError}</p> : null}
            {loginError ? <p className="empty">{loginError}</p> : null}
            {noneAtAll ? (
              <>
                <div className="manager-clients">
                  <table className="table">
                    <thead>
                      <tr>
                        <th style={{ width: 56 }}>Тип</th>
                        <th style={{ width: 260 }}>Клиент</th>
                        <th style={{ width: 130 }}>Свойство ПА</th>
                        <th style={{ width: 180 }}>Телефон</th>
                        <th style={{ width: 150 }}>Сумма покупок, ₽</th>
                        <th style={{ width: 110 }}>Заказов</th>
                        <th style={{ width: 150 }}>Последний заказ</th>
                      </tr>
                    </thead>
                  </table>
                </div>
                <div className="empty managers-empty managers-empty--tab">
                  <div className="empty__icon"><Icon name="person" size={24} /></div>
                  <div>
                    <p className="empty__title">
                      {!working ? 'Клиентов больше нет' : isConsultant(manager) ? 'Клиентов нет' : 'Клиентов пока нет'}
                    </p>
                    <p className="empty__lead">
                      {!working
                        ? (emptyLead || 'Клиенты переданы при увольнении.')
                        : isConsultant(manager)
                          ? 'Консультант не ведёт своих клиентов.'
                          : 'Клиентов закрепляют за менеджером в «Клиентской базе».'}
                    </p>
                  </div>
                </div>
              </>
            ) : noneFiltered ? (
              <div className="empty managers-empty managers-empty--tab">
                <div className="empty__icon"><Icon name="filter" size={24} tone="inherit" /></div>
                <div>
                  <p className="empty__title">Клиентов не найдено</p>
                  <p className="empty__lead">{TYPE_EMPTY[clientType]}</p>
                </div>
                <button type="button" className="btn btn--secondary" onClick={() => { setClientType('all'); setPage(1); }}>
                  Сбросить фильтры
                </button>
              </div>
            ) : (
              <>
                <div className="manager-clients">
                  <table className="table">
                    <thead>
                      <tr>
                        <th style={{ width: 56 }}>Тип</th>
                        <th style={{ width: 260 }}>
                          <SortHead label="Клиент" field="surname" sort={sort} order={order} onSort={changeSort} />
                        </th>
                        <th style={{ width: 130 }}>Свойство ПА</th>
                        <th style={{ width: 180 }}>
                          <SortHead label="Телефон" field="phone" sort={sort} order={order} onSort={changeSort} />
                        </th>
                        <th style={{ width: 150 }}>
                          <SortHead label="Сумма покупок, ₽" field="purchases_total" sort={sort} order={order} onSort={changeSort} />
                        </th>
                        <th style={{ width: 110 }}>
                          <SortHead label="Заказов" field="orders_count" sort={sort} order={order} onSort={changeSort} />
                        </th>
                        <th style={{ width: 150 }}>
                          <SortHead label="Последний заказ" field="last_order_at" sort={sort} order={order} onSort={changeSort} />
                        </th>
                        {canLogin && working ? <th style={{ width: 52 }} /> : null}
                      </tr>
                    </thead>
                    <tbody>
                      {clients.map((item) => {
                        const legal = isLegal(item);
                        const days = daysUntilBlock(item);
                        const stamp = formatDateTime(item.last_order_at);
                        const statusIcon = legal ? '' : paIcon(item.user_status);
                        const active = item.user_active !== false;
                        return (
                          <tr key={item.id}>
                            <td><Icon name={legal ? 'briefcase' : 'person'} size={18} /></td>
                            <td>
                              <span className="table__name">{clientName(item)}</span>
                              {days ? (
                                <span className="client-warn">
                                  <Icon name="clock" size={12} />
                                  {daysLabel(days)}
                                </span>
                              ) : null}
                            </td>
                            <td>{statusIcon ? <Icon name={statusIcon} size={28} /> : null}</td>
                            <td>{item.phone ? formatPhone(item.phone) || item.phone : ''}</td>
                            <td>{formatCount(item.purchases_total)}</td>
                            <td>{formatCount(item.orders_count)}</td>
                            <td>
                              {stamp.date ? (
                                <span className="stamp">
                                  <span>{stamp.date}</span>
                                  <span>{stamp.time}</span>
                                </span>
                              ) : null}
                            </td>
                            {canLogin && working ? (
                              <td>
                                {active ? (
                                  <button
                                    type="button"
                                    className="icon-button"
                                    aria-label="Войти под клиентом"
                                    onClick={(event) => {
                                      event.stopPropagation();
                                      openLogin(item);
                                    }}
                                  >
                                    <Icon name="sign-in" size={16} />
                                  </button>
                                ) : null}
                              </td>
                            ) : null}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                {clientTotal ? (
                  <footer className="pager">
                    <label className="pager__size">
                      Показывать по
                      <select
                        value={pageSize}
                        onChange={(event) => {
                          setPageSize(Number(event.target.value));
                          setPage(1);
                        }}
                      >
                        <option value={30}>30</option>
                        <option value={50}>50</option>
                        <option value={100}>100</option>
                      </select>
                    </label>
                    <div className="pager__nav">
                      <span>{`${from}–${to} из ${clientTotal}`}</span>
                      <button type="button" className="icon-button" aria-label="Предыдущая страница" disabled={page <= 1} onClick={() => setPage((value) => value - 1)}>
                        <Icon name="chevron-left" size={16} />
                      </button>
                      <PagerPages page={page} pageCount={pageCount} onPage={setPage} />
                      <button type="button" className="icon-button" aria-label="Следующая страница" disabled={page >= pageCount} onClick={() => setPage((value) => value + 1)}>
                        <Icon name="chevron-right" size={16} />
                      </button>
                    </div>
                  </footer>
                ) : null}
              </>
            )}
          </>
        ) : currentTab === 'orders' ? (
          <ManagerOrders managerId={managerId} role={role} readOnly={!working || role === 'guest'} />
        ) : currentTab === 'statistics' ? (
          <ManagerStats managerId={managerId} manager={manager} />
        ) : currentTab === 'feed' ? (
          <ManagerFeed managerId={managerId} canComment={canComment} />
        ) : (
          <div className="manager-tab__head">
            <h2>{TABS.find((item) => item.id === currentTab).label}</h2>
          </div>
        )}
      </article>
      {toast ? createPortal(
        <div className="toast" role="status">
          <span className="toast__icon">
            <Icon name="check-circle" size={18} tone="inherit" />
          </span>
          <div className="toast__copy">
            <p className="toast__title">{toast.title}</p>
          </div>
          <button type="button" className="toast__close" aria-label="Закрыть" onClick={() => setToast(null)}>
            <Icon name="x" size={14} tone="inherit" />
          </button>
        </div>,
        document.body,
      ) : null}
      {moveDialog ? createPortal(
        <MoveRdDialog
          manager={manager}
          fromTitle={divisionTitle}
          target={moveDialog.target}
          outside={moveDialog.outside}
          pending={hrPending}
          error={hrError}
          onClose={() => { setMoveDialog(null); setHrError(''); }}
          onConfirm={() => confirmMove(moveDialog.nextId, moveDialog.target)}
        />,
        document.body,
      ) : null}
      {dismissOpen ? createPortal(
        <DismissDialog
          manager={manager}
          divisionTitle={divisionTitle}
          recipients={recipients}
          pending={hrPending}
          error={hrError}
          onClose={() => { setDismissOpen(false); setHrError(''); }}
          onConfirm={confirmDismiss}
        />,
        document.body,
      ) : null}
      {createOpen ? createPortal(
        <DivisionPropertiesDialog
          mode="create"
          presetHeadId={manager.id}
          presetHeadLabel={personLabel(manager)}
          presetIsHead={isRd(manager)}
          onClose={() => { setCreateOpen(false); setHrError(''); }}
          onSaved={onDivisionCreated}
        />,
        document.body,
      ) : null}
      {trusted ? createPortal(
        <div className="overlay overlay--front">
          <div className="dialog" role="dialog" aria-modal="true">
            <div className="dialog__head">
              <h2>Выберите доверенное лицо</h2>
              <button type="button" className="icon-button" aria-label="Закрыть" onClick={() => setTrusted(null)}>
                <Icon name="x" size={18} />
              </button>
            </div>
            <div className="dialog__body">
              {trusted.people.map((person) => (
                <button
                  key={person.id}
                  type="button"
                  className="menu-row"
                  onClick={() => enterAs(person.id, trusted.legalId)}
                >
                  {`${person.surname || ''} ${person.name || ''}`.trim() || person.phone}
                </button>
              ))}
            </div>
          </div>
        </div>,
        document.body,
      ) : null}
    </section>
  );
}

function BackLink({ sectionCode, onLocation }) {
  return (
    <a
      href={managersPath(sectionCode, { view: 'employees' })}
      className="page-back"
      onClick={(event) => {
        if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) return;
        event.preventDefault();
        onLocation({ view: 'employees' });
      }}
    >
      <Icon name="chevron-left" size={14} tone="inherit" />
      Менеджеры
    </a>
  );
}

function Meta({ label, value, note }) {
  return (
    <div className="meta">
      <span>{label}</span>
      <strong>{value}</strong>
      {note ? <em className="manager-head__note">{note}</em> : null}
    </div>
  );
}

function SortHead({ label, field, sort, order, onSort }) {
  const active = sort === field;
  return (
    <button
      type="button"
      className={active && order === 'desc' ? 'sort is-desc' : 'sort'}
      onClick={() => onSort(field)}
    >
      {label}
      {active ? <Icon name="arrow-up" size={14} /> : null}
    </button>
  );
}

export default ManagerCardPage;
